import dayjs from 'dayjs'
import NP from 'number-precision'

import {
  PaymentPlatformPlatformName,
  ProductsSubAppName,
  ProductsSubUsageType,
} from '../types'

import { usePlanPriceInfo } from './planPriceInfo'
import { useStatusInfo } from './statusInfo'

import type { $Enums } from '@prisma/client'
import type { EPaidProductType, TProduct } from '@/contexts/PlanContext/type'

import { api } from '@/utils/api'
import { EProductType } from '@/contexts/PlanContext/type'
import { getProductItems } from '@/contexts/PlanContext/helpers/getProductItems'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { getProductType } from '@/contexts/PlanContext/helpers/getProductType'
import { isUnlimited } from '@/contexts/PlanContext/helpers/isUnlimited'


import { useMemo, useEffect } from 'react';
export function useSubLog({
  appName,
  enabled,
  onSuccess,
  onError,
}: {
  appName: $Enums.SubLogAppName
  enabled?: boolean
  onSuccess?: () => void
  onError?: ({ errMsg }: { errMsg: string }) => void
}) {
  const sessionInfo = useSessionInfo()
  const { userId } = sessionInfo
  /* 包含匿名登录和正常登录 根据需求随意切换 */
  const isAuth = sessionInfo.isSignIn
  // const isAuth = sessionInfo.sessionAuthenticated
  /* 只包含正常登录 */
  const isSignIn = sessionInfo.isSignIn

  const _enabled = useMemo(() => {
    if (enabled !== undefined) {
      return !!(enabled && isAuth && appName)
    }
    return !!(isAuth && appName)
  }, [enabled, isAuth, appName])

  const {
    data,
    isInitialLoading,
    refetch: _refetch,
    isLoading,
    isFetching,
    isFetched,
    isRefetching,
    remove,
  } = api.subLog.getSubLog.useQuery(
    {
      appName,
    },
    {
      enabled: _enabled,
      onError: async (error) => {
        onError?.({ errMsg: error.message })
      },
      onSuccess: () => {
        onSuccess?.()
      },
      cacheTime: 0,
    },
  )

  const refetch: typeof _refetch = (...params) => {
    if (!isAuth) {
      if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('isAuth is false')
      }
      return Promise.resolve({}) as any
    }
    return _refetch(...params)
  }

  const apiCtx = api.useUtils()

  useEffect(() => {
    // 登录之后刷新套餐
    if (_enabled) {
      apiCtx.subLog.getSubLog.invalidate()
    }
  }, [_enabled, userId])

  /* 是否购买过某个产品，空字符串为特殊处理，可以理解为loading状态，不是最新的后端返回值 */
  const isSub = useMemo(() => {
    if (!isSignIn) {
      return false
    }
    if (
      !isAuth ||
      isInitialLoading ||
      isFetching ||
      isLoading ||
      isRefetching
    ) {
      if (data) {
        data.isSub = '' as any
      }
      return ''
    }
    return typeof data?.isSub === 'boolean' ? data?.isSub : ''
  }, [
    data,
    isAuth,
    isFetching,
    isInitialLoading,
    isLoading,
    isRefetching,
    isSignIn,
  ])

  const currentPlan = useMemo(() => {
    if (!isAuth) {
      return undefined
    }
    return data?.currentPlan || undefined
  }, [data, isAuth])

  const otherPlan = useMemo(() => {
    if (!isAuth) {
      return []
    }
    return data?.otherPlan || []
  }, [data, isAuth])

  const subscriptionId = useMemo(() => {
    return currentPlan?.subscriptionId || ''
  }, [currentPlan])

  /** 付费但不管有没有过期 */
  const hasPlan = useMemo(() => {
    const hasSubProduct = currentPlan?.subProductRelation?.some((relation) => {
      const isProductSubAvailable = !!relation?.productsSub
      const isNotFreePlan = !relation?.productsSub?.productName.includes('Free')
      return isProductSubAvailable && isNotFreePlan
    })

    return !!currentPlan && hasSubProduct
  }, [currentPlan])

  /** 当前套餐是否在有效期内，false 为套餐已过期 */
  const isActiveSub = useMemo(() => {
    return !!data?.isActiveSub
  }, [data])

  /** 用户取消了当前套餐 */
  const cancelAtPeriodEnd = useMemo(() => {
    return !!currentPlan?.cancelAtPeriodEnd
  }, [currentPlan])

  /** 当前套餐激活状态 */
  const hasActivePlan = useMemo(() => {
    return !!currentPlan && isActiveSub
  }, [currentPlan, isActiveSub])

  /** 套餐激活且没有取消 */
  const hasActivePlanAndNotCanceled = useMemo(() => {
    return !!currentPlan && isActiveSub && !cancelAtPeriodEnd
  }, [currentPlan, isActiveSub, cancelAtPeriodEnd])

  /** 用户取消了当前套餐，但是还没过期 */
  const isPackageActivePostCancel = useMemo(() => {
    return !!currentPlan && cancelAtPeriodEnd && isActiveSub
  }, [currentPlan, cancelAtPeriodEnd, isActiveSub])

  const isCanceledExpiredSub = useMemo(() => {
    return cancelAtPeriodEnd && !isActiveSub
  }, [cancelAtPeriodEnd, isActiveSub])

  /**
   * 正常付费用户
   */
  const isNormalUser = useMemo(() => {
    return hasPlan && currentPlan?.customSub === 0
  }, [currentPlan?.customSub, hasPlan])

  /**
   * 非正常付费用户
   */
  const isAbnormalUser = useMemo(() => {
    return hasPlan && currentPlan?.customSub !== 0
  }, [currentPlan?.customSub, hasPlan])

  const statusInfo = useStatusInfo({ currentPlan })
  const { canHandle, isPastDue } = statusInfo

  const creditsPlan = useMemo(() => {
    return currentPlan?.subProductRelation.find((item) =>
      item.productsSub.usageList.find(
        (item) => item.usageType === ProductsSubUsageType.credits,
      ),
    )
  }, [currentPlan])

  const creditsProductType = useMemo(() => {
    if (!creditsPlan) {
      return EProductType.FREE
    }
    const type = getProductType({
      product: creditsPlan.productsSub,
    })
    return type as unknown as EPaidProductType
  }, [creditsPlan])

  const creditsPlanIsUnlimited = useMemo(() => {
    return isUnlimited(creditsProductType)
  }, [creditsProductType])

  const creditsPlanIsUnlimitedAndActive = useMemo(() => {
    return creditsPlanIsUnlimited && isActiveSub
  }, [creditsPlanIsUnlimited, isActiveSub])

  const customerId = useMemo(() => {
    return (
      currentPlan?.customerId ||
      otherPlan.find((item) => !!item.customerId)?.customerId ||
      ''
    )
  }, [currentPlan, otherPlan])

  const duration = useMemo(() => {
    return creditsPlan?.productsSub.duration || 12
  }, [creditsPlan])

  const canCancelPlan = useMemo(() => {
    return !currentPlan?.cancelAtPeriodEnd && canHandle && isNormalUser
  }, [canHandle, currentPlan?.cancelAtPeriodEnd, isNormalUser])

  const items = useMemo(() => {
    const productPriceList = [creditsPlan?.productsSub].filter((item) => !!item)

    return getProductItems({
      productPriceList: productPriceList as unknown as TProduct[],
      appName,
    }).map((item) => {
      return {
        ...item,
        item_category5: customerId,
      }
    })
  }, [appName, creditsPlan?.productsSub, customerId])

  const planPaymentPlatformInfo = useMemo(() => {
    return data?.paymentPlatform
  }, [data])

  // 已订阅用户的支付平台是否可用
  const planPlatformInfoEnable = useMemo(() => {
    return !!planPaymentPlatformInfo?.enable
  }, [planPaymentPlatformInfo])

  const planPlatformInfoVisible = useMemo(() => {
    return !!planPaymentPlatformInfo?.visible
  }, [planPaymentPlatformInfo])

  // 已订阅的订阅平台
  const planPlatformName = useMemo(() => {
    const platformName = planPaymentPlatformInfo?.platformName
    if (platformName === 'Apple') {
      return undefined
    }
    if (platformName === 'Google') {
      return undefined
    }
    return platformName
  }, [planPaymentPlatformInfo])

  const ecommerce = useMemo(() => {
    const price = NP.divide(creditsPlan?.productsSub.price || 0, 100)
    return {
      currency: 'USD',
      transaction_id: `${sessionInfo.userId}_${Date.now()}`,
      appName,
      value: +price.toFixed(2),
      renewal: 1,
      items,
      payment_platform: planPlatformName!,
    }
  }, [
    appName,
    creditsPlan?.productsSub.price,
    items,
    planPlatformName,
    sessionInfo.userId,
  ])

  const allPlans = useMemo(() => {
    // 去重，如果app_name 相同，则取date_created 最大的时间
    const _otherPlan = otherPlan.reduce(
      (acc, cur) => {
        const index = acc.findIndex((item) => item.appName === cur.appName)
        if (index === -1) {
          return [...acc, cur]
        }
        if (dayjs(acc[index].dateCreated).isBefore(cur.dateCreated)) {
          acc[index] = cur
        }
        return acc
      },
      [] as typeof otherPlan,
    )

    return [currentPlan, ..._otherPlan]
  }, [currentPlan, otherPlan])

  // 所有有效期内的套餐
  const validityPeriodPlans = useMemo(() => {
    return allPlans.filter((plan) => {
      return (
        plan &&
        plan?.currentPeriodEnd &&
        dayjs().isBefore(plan?.currentPeriodEnd)
      )
    })
  }, [allPlans])

  const isPaidUser = useMemo(() => {
    return !!validityPeriodPlans.length
  }, [validityPeriodPlans])

  const validityPeriodProducts = useMemo(() => {
    const obj: Record<string, { name: string }[]> = {}
    validityPeriodPlans.forEach((plan) => {
      if (plan && plan.subProductRelation?.length) {
        obj[plan.appName] = []
        plan?.subProductRelation.forEach((item) => {
          if (
            item?.productsSub?.usageList[0]?.usageCount &&
            item?.productsSub?.productName
          ) {
            obj[plan.appName].push({
              name: item?.productsSub?.productName,
            })
          }
        })
      }
    })
    return obj
  }, [validityPeriodPlans])

  const priceInfo = usePlanPriceInfo({
    creditsPlan,
    duration,
  })

  const expirePayment = useMemo(() => {
    return !!data?.expirePayment
  }, [data?.expirePayment])

  const usageList = useMemo(() => {
    return creditsPlan?.productsSub?.usageList || []
  }, [creditsPlan?.productsSub?.usageList])

  const planPlatformNameIsPayInSider = useMemo(() => {
    return planPlatformName === 'Payinsider'
  }, [planPlatformName])

  const canChangePayment = useMemo(() => {
    return (
      hasPlan &&
      isNormalUser &&
      planPlatformName === PaymentPlatformPlatformName.Stripe
    )
  }, [hasPlan, isNormalUser, planPlatformName])

  const planPriceIds = useMemo(() => {
    return (
      currentPlan?.subProductRelation
        .map((item) => item?.productsSub?.priceId)
        .filter((item) => !!item) || []
    )
  }, [currentPlan?.subProductRelation])

  const freePlan = useMemo(() => {
    return data?.freePlan || []
  }, [data?.freePlan])

  const canPayModify = useMemo(() => {
    // return false
    return !planPlatformNameIsPayInSider && planPlatformInfoEnable
  }, [planPlatformInfoEnable, planPlatformNameIsPayInSider])

  const canUpgrade = useMemo(() => {
    return (
      (!isPastDue && isNormalUser && !isPackageActivePostCancel) ||
      !canPayModify
    )
  }, [canPayModify, isNormalUser, isPackageActivePostCancel, isPastDue])

  const canReactivate = useMemo(() => {
    return isPackageActivePostCancel && canPayModify
  }, [isPackageActivePostCancel, canPayModify])

  return {
    /** 是否订阅 */
    isSub,
    /** 免费用户的套餐 */
    freePlan,
    /** 当前的套餐 */
    currentPlan,
    /** credits 套餐详情 */
    creditsPlan,
    /** 当前是否有套餐 */
    hasPlan,
    /** 当前有套餐未过期  */
    hasActivePlan,
    /** 当前有套餐未过期，没取消  */
    hasActivePlanAndNotCanceled,
    isNormalUser,
    isAbnormalUser,
    canHandle,
    loading: isInitialLoading,
    isDataLoading: isLoading,
    refetch,
    isFetching,
    isRefetching,
    subscriptionId,
    isFetched,
    otherPlan,
    isActiveSub,
    isPackageActivePostCancel,
    isCanceledExpiredSub,
    cancelAtPeriodEnd,
    creditsPlanIsUnlimited,
    customerId,
    duration,
    canCancelPlan,
    creditsProductType,
    ecommerce,
    creditsPlanIsUnlimitedAndActive,
    validityPeriodPlans,
    isPaidUser,
    validityPeriodProducts,
    priceInfo,
    statusInfo,
    expirePayment,
    usageList,
    planPlatformInfoEnable,
    planPlatformName,
    canChangePayment,
    planPriceIds,
    planPlatformNameIsPayInSider,
    isPastDue,
    canPayModify,
    canReactivate,
    canUpgrade,
    planPlatformInfoVisible,
    remove,
  }
}

export type TUseSubLog = ReturnType<typeof useSubLog>

/* 仅做类型使用 */
function useSubLogType() {
  const { data } = api.subLog.getSubLog.useQuery(
    {
      appName: ProductsSubAppName.App1,
    },
    {
      enabled: false,
    },
  )
  const currentPlan = useMemo(() => {
    return data?.currentPlan || undefined
  }, [data])

  const productsSub = useMemo(() => {
    return currentPlan?.subProductRelation[0] || undefined
  }, [currentPlan])

  return {
    currentPlan,
    productsSub,
  }
}

export type TUseSubLogType = ReturnType<typeof useSubLogType>
